import tinycolor from 'tinycolor2';

const primary = '#536DFE';
const secondary = '#FF5C93';
const warning = '#FFC260';
const success = '#3CD4A0';
const info = '#9013FE';
const gray = '#4A4A4A';
const pink = '#DE369D';
const red = '#FE5F55';
const brown = '#7A4419';
const cian = '#59F8E8';
const burgundy = '#B497D6';
const violet = '#241E4E';
const vanila = '#F0F2A6';

const lightenRate = 7.5;
const darkenRate = 15;

const makeColor = (color) => ({
  main: color,
  light: tinycolor(color).lighten(lightenRate).toHexString(),
  dark: tinycolor(color).darken(darkenRate).toHexString(),
});

// Mode-aware neutral palette. Brand colors stay identical across modes.
const palettes = {
  light: {
    text: {
      primary: '#1A2027',
      secondary: '#5A6472',
      hint: '#9AA4B2',
    },
    background: {
      default: '#F4F6FB',
      paper: '#FFFFFF',
      // custom key consumed across the app for hover / subtle surfaces
      light: '#EEF1F8',
      // translucent scrim used behind loading spinners (loaderWrap)
      overlay: 'rgba(255, 255, 255, 0.7)',
    },
    divider: 'rgba(16, 24, 40, 0.08)',
    backdrop: 'rgba(26, 32, 39, 0.4)',
  },
  dark: {
    text: {
      primary: '#E6EAF0',
      secondary: '#9AA4B2',
      hint: '#6B7480',
    },
    background: {
      default: '#0F141A',
      paper: '#1A2027',
      light: '#252D38',
      overlay: 'rgba(15, 20, 26, 0.7)',
    },
    divider: 'rgba(255, 255, 255, 0.08)',
    backdrop: 'rgba(0, 0, 0, 0.6)',
  },
};

const shadowSets = {
  light: {
    widget: '0 1px 3px rgba(16, 24, 40, 0.06), 0 1px 2px rgba(16, 24, 40, 0.04)',
    widgetDark: '0 8px 24px rgba(83, 109, 254, 0.28)',
    widgetWide: '0 8px 24px rgba(16, 24, 40, 0.08), 0 2px 6px rgba(16, 24, 40, 0.04)',
  },
  dark: {
    widget: '0 1px 3px rgba(0, 0, 0, 0.45), 0 1px 2px rgba(0, 0, 0, 0.35)',
    widgetDark: '0 8px 24px rgba(0, 0, 0, 0.55)',
    widgetWide: '0 10px 28px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.35)',
  },
};

export default function createDefaultTheme(mode = 'light') {
  const isDark = mode === 'dark';
  const neutral = palettes[isDark ? 'dark' : 'light'];
  const customShadows = shadowSets[isDark ? 'dark' : 'light'];
  const radius = 12;

  return {
    palette: {
      type: mode,
      primary: makeColor(primary),
      secondary: { ...makeColor(secondary), contrastText: '#FFFFFF' },
      warning: makeColor(warning),
      success: makeColor(success),
      info: makeColor(info),
      grey: makeColor(gray),
      pink: makeColor(pink),
      red: makeColor(red),
      brown: makeColor(brown),
      cian: makeColor(cian),
      burgundy: makeColor(burgundy),
      violet: makeColor(violet),
      vanila: makeColor(vanila),
      text: neutral.text,
      background: neutral.background,
      divider: neutral.divider,
    },
    shape: {
      borderRadius: radius,
    },
    customShadows,
    overrides: {
      MuiCssBaseline: {
        '@global': {
          body: {
            backgroundColor: neutral.background.default,
            color: neutral.text.primary,
          },
        },
      },
      MuiBackdrop: {
        root: {
          backgroundColor: neutral.backdrop,
        },
      },
      MuiPaper: {
        rounded: {
          borderRadius: radius,
        },
      },
      MuiButton: {
        root: {
          textTransform: 'none',
          borderRadius: 10,
          fontWeight: 500,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: customShadows.widget,
          },
        },
      },
      MuiOutlinedInput: {
        root: {
          borderRadius: 10,
        },
      },
      MuiMenu: {
        paper: {
          borderRadius: radius,
          boxShadow: customShadows.widgetWide,
        },
      },
      MuiSelect: {
        icon: {
          color: neutral.text.hint,
        },
      },
      MuiListItem: {
        root: {
          '&$selected': {
            backgroundColor: `${neutral.background.light} !important`,
            '&:focus': {
              backgroundColor: neutral.background.light,
            },
          },
        },
        button: {
          '&:hover, &:focus': {
            backgroundColor: neutral.background.light,
          },
        },
      },
      MuiTouchRipple: {
        child: {
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.4)' : 'white',
        },
      },
      MuiTableRow: {
        root: {
          height: 56,
        },
      },
      MuiTableCell: {
        root: {
          borderBottom: `1px solid ${neutral.divider}`,
          paddingLeft: 24,
        },
        head: {
          fontSize: '0.95rem',
        },
        body: {
          fontSize: '0.95rem',
        },
      },
      PrivateSwitchBase: {
        root: {
          marginLeft: 10,
        },
      },
    },
  };
}
